import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { getVideos } from "../../../../services/video";
import { Video } from "../../../../interfaces/video";
import { Button, Col, Form, Row } from "react-bootstrap";
import { AdminVideoNew } from "./video-new";
import { AdminVideoDelete } from "./video-delete";
import { AdminVideoEdit } from "./video-edit";
import { UserContext } from "../../../../App";
import { LoadingSpinner } from "../../../general/loading-spinner";
import { getClubOptions } from "../../../../services/club";
import { getUser } from "../../../../services/user";
import { Club } from "../../../../interfaces/club";
import { User } from "../../../../interfaces/user";
import moment from 'moment';

export function AdminVideoList() {
    const { userProfile } = useContext(UserContext);

    const is_administrator = (userProfile.role.filter((r: any) => r.id == 'badminton_administrator').length > 0);
    const is_manager = (userProfile.role.filter((r: any) => r.id == 'badminton_manager').length > 0);
    const is_coach = (userProfile.role.filter((r: any) => r.id == 'badminton_coach').length > 0);

    const [videos, setVideos] = useState<Video[]>([]);
    const [showAddNew, setShowAddNew] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMoreVideos, setHasMoreVideos] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [loaded, setLoaded] = useState(false);
    const [clubs, setClubs] = useState<Club[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [selectedClubId, setSelectedClubId] = useState(0);
    const [selectedUserId, setSelectedUserId] = useState(0);
    const [loadingUsers, setLoadingUsers] = useState(false);

    useEffect(() => {
        Promise.allSettled([
            getVideos('', 1, 12),
            getClubOptions()
        ]).then(response => {
            // Videos
            if (response[0].status == 'fulfilled') {
                setVideos(response[0].value.data);
                setHasMoreVideos(response[0].value.data.length === 12);
                setCurrentPage(1);
            }
            // Clubs
            if (response[1].status == 'fulfilled') {
                setClubs(response[1].value.data);
            }
            setLoaded(true);
        }).catch(error => {
            console.log(error);
        });
    }, []);

    const handleClubChange = (event: any) => {
        const clubId = parseInt(event.target.value || '0');
        setSelectedClubId(clubId);
        setSelectedUserId(0);
        setUsers([]);

        if (clubId > 0) {
            // Cargar usuarios del club seleccionado
            setLoadingUsers(true);
            getUser('', clubId.toString())
                .then(response => {
                    setUsers(response.data);
                    setLoadingUsers(false);
                })
                .catch(error => {
                    console.log(error);
                    setLoadingUsers(false);
                });
        }
    }

    const handleSearchSubmit = (event: any) => {
        event.preventDefault();

        const s = event.target.search.value;
        const clubId = selectedClubId;
        const userId = selectedClubId > 0 && event.target.user ? parseInt(event.target.user.value || '0') : 0;

        setSearchTerm(s);
        setSelectedUserId(userId);

        getVideos(s, 1, 12, clubId, userId)
            .then(response => {
                setVideos(response.data);
                setHasMoreVideos(response.data.length === 12);
                setCurrentPage(1);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const handleAddNew = (event: any) => {
        event.preventDefault();
        setShowAddNew(true)
    }

    const handleAddNewOnSave = (s: Video) => {
        const newVideos = [
            s,
            ...videos
        ];
        setVideos(newVideos);
        setShowAddNew(false);
    }

    const handleAddNewOnClose = () => {
        setShowAddNew(false);
    }

    const handleDelete = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowDelete(true);
    }

    const handleDeleteOnDelete = (id: number) => {
        setSelectedId(0);
        const newVideos = videos.filter((s: Video) => s.id != id);
        setVideos(newVideos);
        setShowDelete(false);
    }

    const handleDeleteOnClose = () => {
        setSelectedId(0);
        setShowDelete(false);
    }

    const handleEdit = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowEdit(true);
    }

    const handleEditOnSave = (changedVideo: Video) => {
        setSelectedId(0);
        const newVideos = videos.map((v: Video) => {
            if (v.id == changedVideo.id) {
                return changedVideo;
            } else {
                return v;
            }
        });
        setVideos(newVideos);
        setShowEdit(false);
    }

    const handleEditOnClose = () => {
        setSelectedId(0);
        setShowEdit(false);
    }

    const handleLoadMore = () => {
        if (isLoadingMore || !hasMoreVideos) return;

        setIsLoadingMore(true);
        const nextPage = currentPage + 1;

        getVideos(searchTerm, nextPage, 12, selectedClubId, selectedUserId)
            .then(response => {
                setVideos(prevVideos => [...prevVideos, ...response.data]);
                setCurrentPage(nextPage);
                setHasMoreVideos(response.data.length === 12);
                setIsLoadingMore(false);
            })
            .catch(error => {
                console.log(error);
                setIsLoadingMore(false);
            });
    }

    return (
        <div className="video-list-container">
            <h1>
                { t('videos')}
            </h1>
            { !loaded && <LoadingSpinner /> }
            { loaded &&
                <div className="filters-container">
                    <Form onSubmit={ handleSearchSubmit }>
                        <Form.Group className="field-search" controlId="search">
                            <Form.Control type="search" placeholder={ t("Write to search") } defaultValue={searchTerm} />
                        </Form.Group>
                        <Form.Group controlId="club">
                            <Form.Select value={selectedClubId} onChange={handleClubChange}>
                                <option value="0">{ t("- All clubs -") }</option>
                                { clubs.map((club: Club) => (
                                    <option key={club.id} value={club.id}>
                                        { club.name }
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        { selectedClubId > 0 && !loadingUsers && users.length > 0 && (
                            <Form.Group controlId="user">
                                <Form.Select value={selectedUserId} onChange={(e) => setSelectedUserId(parseInt(e.target.value || '0'))}>
                                    <option value="0">{ t("- All users -") }</option>
                                    { users.map((user: User) => (
                                        <option key={user.id} value={user.id}>
                                            { user.name }
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        )}
                        <div className="actions">
                            <Button variant="primary" type="submit">
                                { t("search") }
                            </Button>
                        </div>
                    </Form>
                </div>
            }
            { loaded &&
                <div className="grid-container">
                    <Row>
                        { videos.map((v: Video) => {
                            return <Col key={ 'video-' + v.id } xs={12} md={6} lg={4} xl={3}>
                                <div className="video-card">
                                    <div className="heading">
                                        <div className="media">
                                            { v.media.length > 0 &&
                                                <video controls>
                                                    <source src={ v.media[0].url } />
                                                </video>
                                            }
                                        </div>
                                        <div className="meta">
                                            <div className="name">
                                                { v.name }
                                            </div>
                                            <div className="info">
                                                <small className="text-muted">
                                                    <div>
                                                        <i className="bi bi-person"></i> { v.created_by_name }
                                                    </div>
                                                    <div>
                                                        <i className="bi bi-calendar"></i> { moment(v.created_at).format("DD/MM/YYYY") }
                                                    </div>
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="actions">
                                        { (is_administrator || is_manager || (is_coach && v.created_by == userProfile.id)) &&
                                            <a href="#" className="icon-link edit" title={ t("edit") } onClick={ handleEdit } data-id={ v.id }>
                                                <i className="bi bi-pencil-square"></i>
                                            </a>
                                        }
                                        { (is_administrator || is_manager || (is_coach && v.created_by == userProfile.id)) &&
                                            <a href="#" className="icon-link delete" title={ t("edit") } onClick={ handleDelete } data-id={ v.id }>
                                                <i className="bi bi-trash"></i>
                                            </a>
                                        }
                                    </div>
                                </div>
                            </Col>
                        })}
                    </Row>
                    { hasMoreVideos && (
                        <Row className="mt-4">
                            <Col className="text-center">
                                <Button
                                    variant="outline-primary"
                                    onClick={handleLoadMore}
                                    disabled={isLoadingMore}
                                >
                                    {isLoadingMore ? t('loading') + '...' : t('Load more')}
                                </Button>
                            </Col>
                        </Row>
                    )}
                </div>
            }
            { loaded && 
                <a className="admin-add-new" href="#" onClick={ handleAddNew }>
                    <i className="bi bi-plus-circle"></i>
                </a>
            }
            { showAddNew && 
                <AdminVideoNew onSave={ handleAddNewOnSave } onClose={ handleAddNewOnClose } />
            }
            { showDelete && (selectedId != 0) &&
                <AdminVideoDelete id={ selectedId } onDelete={ handleDeleteOnDelete } onClose={ handleDeleteOnClose } />
            }
            { showEdit && (selectedId != 0) &&
                <AdminVideoEdit id={ selectedId } onSave={ handleEditOnSave } onClose={ handleEditOnClose } />
            }
        </div>

    )
}